/**
 * "See my statements" on My metrics (spec "Statement archive"; Asana "See my statements on
 * My metrics"): the signed-in person's monthly scorecard PDFs, newest first, each with Open
 * and Download.
 *
 * Always the person signed in, whoever the page is showing: a statement is a document sent
 * to one person (api/shared/statements.ts decides whose). The button appears only when
 * there is something to list, so while employee delivery is off, and before anyone has been
 * sent anything, nothing shows. Links are five-minute read-only links the API issues per
 * click; nothing here is a stored URL.
 */
import { useEffect, useState } from 'react'
import { useOptionalAuth } from '@/auth/AuthProvider'
import { apiFetch } from '@/auth/apiFetch'
import { TOKEN_HEADER } from '@/data/client'
import { monthYear } from '@/format'

export interface StatementItem {
  id: number
  month: string
  kind: 'employee' | 'team' | 'department'
  employee: string
  fileName: string
  score: number | null
  rankText: string | null
  version: number
  status: 'generated' | 'held' | 'sent' | 'corrected' | 'withdrawn'
  sentAt: string | null
  dryRun?: boolean
}

export const KIND_LABEL: Record<StatementItem['kind'], string> = {
  employee: 'My scorecard',
  team: 'Team scorecard',
  department: 'Department scorecard',
}

const points = (v: number | null) => (v === null ? '—' : `${v > 0 ? '+' : ''}${Number.isInteger(v) ? v : v.toFixed(1)}`)
const sentOn = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

function useMyStatements(): StatementItem[] | null {
  const auth = useOptionalAuth()
  const token = auth?.token ?? null
  const ready = auth?.ready ?? false
  const [items, setItems] = useState<StatementItem[] | null>(null)
  useEffect(() => {
    if (!ready) return
    let cancelled = false
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (token) headers[TOKEN_HEADER] = token
    apiFetch('/api/statements', { headers })
      .then((r) => (r.ok ? (r.json() as Promise<{ statements?: StatementItem[] }>) : null))
      .then((body) => !cancelled && setItems(Array.isArray(body?.statements) ? body.statements : null))
      // 403 (page-limited), 503 (not set up yet), an older API: no button.
      .catch(() => !cancelled && setItems(null))
    return () => {
      cancelled = true
    }
  }, [token, ready])
  return items
}

export function MyStatements() {
  const items = useMyStatements()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const token = useOptionalAuth()?.token ?? null
  if (!items || items.length === 0) return null

  const fetchLink = async (s: StatementItem, purpose: 'open' | 'download') => {
    setBusy(s.id)
    setError(null)
    // Opened before the request, so the browser does not treat it as a pop-up.
    const tab = purpose === 'open' ? window.open('', '_blank') : null
    try {
      const headers: Record<string, string> = { Accept: 'application/json' }
      if (token) headers[TOKEN_HEADER] = token
      const r = await apiFetch(`/api/statements/${s.id}/${purpose}`, { headers })
      if (!r.ok) throw new Error(String(r.status))
      const { url } = (await r.json()) as { url: string }
      if (tab) tab.location.href = url
      else {
        const a = document.createElement('a')
        a.href = url
        a.download = s.fileName
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
    } catch {
      tab?.close()
      setError('That statement could not be opened. Try again in a moment.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <button type="button" className="btn" aria-expanded={open} onClick={() => setOpen((v) => !v)} style={{ alignSelf: 'flex-end' }}>
        {open ? 'Hide my statements' : 'See my statements'}
      </button>
      {open && (
        // Its own full-width row under the header's controls (the header wraps), so the
        // list never runs off the page at any width; wide tables scroll inside the card.
        <div className="card" role="region" aria-label="My statements" style={{ flexBasis: '100%', order: 99, maxWidth: 820, overflowX: 'auto' }}>
          <table className="dt" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Month</th>
                <th>Statement</th>
                <th style={{ textAlign: 'right' }}>Score</th>
                <th style={{ textAlign: 'right' }}>Rank</th>
                <th>Sent</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id}>
                  <td>{monthYear(s.month)}</td>
                  <td>
                    {KIND_LABEL[s.kind]}
                    {s.status === 'corrected' && <span className="eyebrow" style={{ marginLeft: 6 }}>corrected</span>}
                    {s.dryRun && <span className="eyebrow" style={{ marginLeft: 6 }}>dry run</span>}
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {points(s.score)}
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {s.rankText ?? '—'}
                  </td>
                  <td>{sentOn(s.sentAt)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button type="button" className="btn" disabled={busy === s.id} onClick={() => fetchLink(s, 'open')}>
                      Open
                    </button>{' '}
                    <button type="button" className="btn" disabled={busy === s.id} onClick={() => fetchLink(s, 'download')}>
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {error && (
            <p className="body-note" role="alert" style={{ margin: '8px 0 0', color: 'var(--crit-ink)' }}>
              {error}
            </p>
          )}
        </div>
      )}
    </>
  )
}
